import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppConstants } from '../../constants/AppConstants';
import InputField from '../shared/Fields/InputField';
import Card from '../shared/cards/Card';
import UserRole from '../../assets/user-cog.svg';
import ReactDatePicker from 'react-datepicker';
import { DateInputField } from '../Reusable/DateInputField';
import { PencilIcon, PlusCircleIcon, XCircleIcon, CheckCircleIcon } from '@heroicons/react/outline';
import moment from 'moment';
import { Tag } from './FeatureDetails';
import { IconButton } from '../primitives/buttons/IconButton';

interface Props {
  tags: Array<any>;
  setTags: Function;
  disabled: boolean;
  tagInput: Tag;
  setTagInput: Function;
}

interface CardProps {
  key: any;
  name: string;
  number: number;
  expiredOn: string | null;
  onEdit: () => void;
  onDelete: () => void;
  isLast: boolean;
  disabled: boolean;
}

const SelectedCard = ({
  name,
  number,
  expiredOn,
  onEdit,
  onDelete,
  isLast,
  disabled
}: CardProps) => {
  const [showPopover, setShowPopover] = useState(false);
  const nameRef = useRef(null);

  const truncateName = (str: string, num: number) => {
    if (str.length <= num) return str;
    return str.slice(0, num) + '...';
  };

  return (
    <div className={`flex w-auto flex-col p-2 ${!isLast && 'mb-2.5'}`}>
      <div className="flex flex-col rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-lg">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center">
            <p className="text-lg font-semibold text-black">{number}.</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onEdit}
              disabled={disabled}
              className={`text-blue-500 hover:text-blue-700 focus:outline-none ${
                disabled ? 'cursor-not-allowed opacity-50' : ''
              }`}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={onDelete}
              disabled={disabled}
              className={`text-red-500 hover:text-red-700 focus:outline-none ${
                disabled ? 'cursor-not-allowed opacity-50' : ''
              }`}
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex flex-col space-y-1">
          <div className="flex items-center">
            <span className="flex-shrink-0 font-medium text-gray-600">Key:</span>
            <span
              ref={nameRef}
              className="relative ml-2 text-gray-800"
              onMouseEnter={() => setShowPopover(true)}
              onMouseLeave={() => setShowPopover(false)}
            >
              {truncateName(name, 80)}
              {showPopover && name.length > 80 && (
                <div className="absolute left-0 top-full z-10 mt-1 max-h-32 w-96 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 shadow-lg">
                  <p className="break-words text-sm">{name}</p>
                </div>
              )}
            </span>
          </div>
          <div className="flex items-center">
            <span className="flex-shrink-0 font-medium text-gray-600">Expires On:</span>
            <span className="ml-2 text-gray-800">
              {expiredOn
                ? moment(expiredOn).format(AppConstants.DEFAULT_DATE_FORMAT)
                : 'No Expiration'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
const Tags = ({ tags = [], setTags, disabled, tagInput, setTagInput }: Props) => {
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [initialTagInput, setInitialTagInput] = useState<Tag | null>(null);

  useEffect(() => {
    if (editingIndex !== null) {
      setInitialTagInput(tags[editingIndex]);
    }
  }, [editingIndex, tags]);

  useEffect(() => {
    if (tagInput?.key || (tagInput?.expiredOn && !isAdding)) {
      setIsAdding(true);
    }
  }, []);

  const handleTagAddOrEdit = () => {
    let currentTags = [...tags];
    let tagObj = { ...tagInput };
    if (tagObj?.expiredOn === null) {
      delete tagObj.expiredOn;
    }

    if (editingIndex !== null) {
      currentTags[editingIndex] = tagObj;
    } else {
      currentTags.push(tagObj);
    }

    setTags(currentTags);
    setTagInput({ key: '', expiredOn: null });
    setIsAdding(false);
    setEditingIndex(null);
    setInitialTagInput(null);
  };

  const handleTagEdit = (index: number) => {
    setTagInput(tags[index]);
    setEditingIndex(index);
    setIsAdding(true);
  };

  const handleTagDelete = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const tagValidator = () => {
    return !tagInput.key.length || disabled || tagInput?.key.length > 1024;
  };

  const hasChanged = () => {
    return editingIndex !== null
      ? JSON.stringify(tagInput) !== JSON.stringify(initialTagInput)
      : true;
  };

  return (
    <div className="mt-4 h-full w-full">
      <Card
        title={t('T_FEATURE_TAGS')}
        icon={UserRole}
        alt={t('T_FEATURE_TAGS')}
        height=""
        width=""
        actions={
          !isAdding ? (
            <IconButton
              className={`!mt-0 ml-auto mr-2 !border-none !px-2 text-primary ${
                disabled ? 'cursor-not-allowed opacity-50' : ''
              }`}
              onClick={() => {
                setIsAdding(true);
              }}
              disabled={disabled}
            >
              <span className="flex w-full flex-row p-1">
                <PlusCircleIcon
                  className="mr-1 h-7 w-7 text-primary"
                  data-testid="add-feature-tag-btn"
                />
              </span>
            </IconButton>
          ) : (
            <></>
          )
        }
      >
        <div className="flex w-full flex-col">
          {isAdding && (
            <div className="mb-4 flex flex-row gap-4">
              <div className="flex w-1/2 flex-col">
                <InputField
                  type="text"
                  label={t('T_FEATURE_TAGS_KEY')}
                  placeholder=""
                  required
                  id="tagKey"
                  formData={tagInput as any}
                  fieldKey="key"
                  setFormData={setTagInput}
                  formError={{}}
                  disabled={disabled}
                  popoverContent={
                    <div className="w-48">
                      <p className="text-center text-sm">{t('T_FEATURE_TAGS_KEY_LIMIT')}</p>
                    </div>
                  }
                />
              </div>
              <div className="flex w-1/2 flex-col">
                <ReactDatePicker
                  selected={tagInput.expiredOn ? new Date(tagInput.expiredOn) : null}
                  onChange={(date: Date) =>
                    setTagInput({ ...tagInput, expiredOn: date.toISOString() })
                  }
                  className="w-1/2 rounded border"
                  fixedHeight
                  showTimeSelect
                  minDate={new Date()}
                  showMonthDropdown
                  showYearDropdown
                  scrollableYearDropdown
                  showDisabledMonthNavigation
                  disabled={disabled}
                  shouldCloseOnSelect={false}
                  yearDropdownItemNumber={100}
                  calendarClassName={'text-base'}
                  customInput={
                    <DateInputField
                      key={tagInput.expiredOn}
                      overrideValue={
                        tagInput.expiredOn
                          ? moment(tagInput.expiredOn).format(
                              AppConstants.DEFAULT_CAREPLAN_DATE_TIME_FORMAT
                            )
                          : ''
                      }
                      fieldKey="expiredOn"
                      formData={tagInput}
                      formError={{}}
                      label={t('T_FEATURE_TAGS_EXPIRY_HEADER')}
                    />
                  }
                />
              </div>
              <div className="flex items-center">
                <IconButton
                  onClick={handleTagAddOrEdit}
                  disabled={tagValidator() || (editingIndex !== null && !hasChanged())}
                  className={`!border-none text-primary ${
                    tagValidator() || (editingIndex !== null && !hasChanged())
                      ? 'cursor-not-allowed opacity-50'
                      : ''
                  }`}
                >
                  {editingIndex !== null ? (
                    <CheckCircleIcon className="text-green-500 h-8 w-8" />
                  ) : (
                    <PlusCircleIcon className="h-8 w-8 text-primary" />
                  )}
                </IconButton>
              </div>
            </div>
          )}
          <div className="mt-4 flex w-full flex-wrap gap-4">
            {tags?.length > 0 &&
              tags.map((tag: any, index: number) => (
                <SelectedCard
                  key={index}
                  name={tag?.key}
                  expiredOn={tag?.expiredOn}
                  number={index + 1}
                  onEdit={() => handleTagEdit(index)}
                  onDelete={() => handleTagDelete(index)}
                  isLast={index === tags.length - 1}
                  disabled={disabled}
                />
              ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Tags;
